import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const SHOP_API =
    "https://oppiesfunc.azurewebsites.net/api/getShopByEmail?code=7KWINbztw9fLYDfl2dC2hQ-oOEljDDO170DmZm61jttOAzFuZHRDLw==";

const SALES_API =
    "https://oppiesfunc.azurewebsites.net/api/getSalesByShopId?code=AJJ6BhJCb7kfUYKCWu8rt5Vy_4LnvfKV4uHNTgDaWrw8AzFuw07_8Q==";

const REVIEW_SUMMARY_API =
    "https://oppiesfunc.azurewebsites.net/api/getReviewSummaryByShop?code=M9HVWubTSwX3ubDuBmCFG1Sc55WElwR7AOxcBNlXEqh_AzFuSRKMxA==";

const REVIEWS_API =
    "https://oppiesfunc.azurewebsites.net/api/getReviewsByShop?code=DoHf7NWbvGWUlR3EADcU1aU9aM4pMnOYTivml1Ky27xRAzFuTKLPMQ==";

const IMAGE_API =
    "https://oppiesfunc.azurewebsites.net/api/getShopImageById?code=cGJu1RhQotBezE1TOQ6Y29Xjan_uI55kgdtNTy8j83ZpAzFupbeKAw==";

const PROFILE_ANALYTICS_API =
    "https://oppiesfunc.azurewebsites.net/api/getShopProfileAnalytics?code=6a_3MCupiHQL9GqwTCaAen_n9H21n4sdHPZ3HpEytQadAzFuAy3Gpg==";


const LineChart = ({
    data = [],
    minY = 0,
    maxY,
    valueFormatter = (value) => value,
    emptyTitle = "No data available yet",
    emptyText = "",
}) => {
    if (!Array.isArray(data) || data.length === 0) {
        return (
            <div className="overview-chart-empty">
                <div className="overview-chart-empty-icon">
                    <svg
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M3 3v18h18" />
                        <path d="m7 15 4-4 3 3 5-6" />
                    </svg>
                </div>

                <div className="overview-chart-empty-title">
                    {emptyTitle}
                </div>

                {emptyText ? (
                    <div className="overview-chart-empty-text">
                        {emptyText}
                    </div>
                ) : null}
            </div>
        );
    }

    const width = 760;
    const height = 260;

    const padding = {
        top: 20,
        right: 22,
        bottom: 42,
        left: 48,
    };

    const chartWidth =
        width - padding.left - padding.right;

    const chartHeight =
        height - padding.top - padding.bottom;

    const values = data.map(
        (item) => Number(item.value) || 0
    );

    const rawMaximum =
        maxY !== undefined
            ? Number(maxY)
            : Math.max(...values, minY + 1);

    let chartMaximum = rawMaximum;

    if (maxY === undefined) {
        chartMaximum =
            rawMaximum <= 5
                ? Math.max(5, rawMaximum)
                : Math.ceil(rawMaximum * 1.15);
    }

    if (chartMaximum <= minY) {
        chartMaximum = minY + 1;
    }

    const getX = (index) => {
        if (data.length === 1) {
            return padding.left + chartWidth / 2;
        }

        return (
            padding.left +
            (index / (data.length - 1)) *
            chartWidth
        );
    };

    const getY = (value) => {
        const safeValue = Math.max(
            minY,
            Math.min(chartMaximum, Number(value) || 0)
        );

        const percent =
            (safeValue - minY) /
            (chartMaximum - minY);

        return (
            padding.top +
            chartHeight -
            percent * chartHeight
        );
    };

    const points = data
        .map(
            (item, index) =>
                `${getX(index)},${getY(item.value)}`
        )
        .join(" ");

    const areaPoints = [
        `${padding.left},${padding.top + chartHeight
        }`,
        points,
        `${padding.left + chartWidth},${padding.top + chartHeight
        }`,
    ].join(" ");

    const gridLines = 4;

    const yTicks = Array.from(
        { length: gridLines + 1 },
        (_, index) => {
            const ratio = index / gridLines;

            const value =
                chartMaximum -
                ratio * (chartMaximum - minY);

            const y =
                padding.top +
                ratio * chartHeight;

            return {
                y,
                value,
            };
        }
    );

    const middleIndex = Math.floor(
        (data.length - 1) / 2
    );

    let dateLabels;

    if (data.length === 1) {
        dateLabels = [
            {
                index: 0,
                anchor: "middle",
            },
        ];
    } else if (data.length === 2) {
        dateLabels = [
            {
                index: 0,
                anchor: "start",
            },
            {
                index: 1,
                anchor: "end",
            },
        ];
    } else {
        dateLabels = [
            {
                index: 0,
                anchor: "start",
            },
            {
                index: middleIndex,
                anchor: "middle",
            },
            {
                index: data.length - 1,
                anchor: "end",
            },
        ];
    }

    const formatDateLabel = (value) => {
        if (!value) return "";

        const parsed = new Date(
            `${value}T00:00:00`
        );

        if (Number.isNaN(parsed.getTime())) {
            return value;
        }

        return parsed.toLocaleDateString(
            "en-NZ",
            {
                day: "numeric",
                month: "short",
                year:
                    data.length > 60
                        ? "2-digit"
                        : undefined,
            }
        );
    };

    return (
        <div className="overview-chart-wrap">
            <svg
                className="overview-chart-svg"
                viewBox={`0 0 ${width} ${height}`}
                role="img"
                aria-label="Analytics chart"
            >
                <defs>
                    <linearGradient
                        id="oppiesChartArea"
                        x1="0"
                        x2="0"
                        y1="0"
                        y2="1"
                    >
                        <stop
                            offset="0%"
                            stopColor="#047857"
                            stopOpacity="0.18"
                        />

                        <stop
                            offset="100%"
                            stopColor="#047857"
                            stopOpacity="0"
                        />
                    </linearGradient>
                </defs>

                {yTicks.map((tick, index) => (
                    <g key={index}>
                        <line
                            x1={padding.left}
                            x2={
                                padding.left + chartWidth
                            }
                            y1={tick.y}
                            y2={tick.y}
                            stroke="#e5e7eb"
                            strokeWidth="1"
                        />

                        <text
                            x={padding.left - 10}
                            y={tick.y + 4}
                            textAnchor="end"
                            fontSize="11"
                            fill="#94a3b8"
                        >
                            {valueFormatter(
                                tick.value
                            )}
                        </text>
                    </g>
                ))}

                <polygon
                    points={areaPoints}
                    fill="url(#oppiesChartArea)"
                />

                <polyline
                    points={points}
                    fill="none"
                    stroke="#047857"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {data.map((item, index) => (
                    <circle
                        key={`${item.date}-${index}`}
                        cx={getX(index)}
                        cy={getY(item.value)}
                        r="4"
                        fill="#ffffff"
                        stroke="#047857"
                        strokeWidth="2"
                    >
                        <title>
                            {`${item.date}: ${valueFormatter(
                                item.value
                            )}`}
                        </title>
                    </circle>
                ))}

                {dateLabels.map(
                    ({ index, anchor }, labelIndex) => {
                        const item = data[index];

                        if (!item) return null;

                        return (
                            <text
                                key={`${index}-${labelIndex}`}
                                x={getX(index)}
                                y={height - 12}
                                textAnchor={anchor}
                                fontSize="11"
                                fill="#64748b"
                            >
                                {formatDateLabel(
                                    item.date
                                )}
                            </text>
                        );
                    }
                )}
            </svg>
        </div>
    );
};


const Overview = ({ shopEmail }) => {
    const [shop, setShop] =
        useState(null);

    const [sales, setSales] =
        useState([]);

    const [
        reviewSummary,
        setReviewSummary,
    ] = useState(null);

    const [
        reviewHistory,
        setReviewHistory,
    ] = useState([]);

    const [imageUrl, setImageUrl] =
        useState("");

    const [
        profileAnalytics,
        setProfileAnalytics,
    ] = useState(null);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");


    useEffect(() => {
        const fetchAll = async () => {
            if (!shopEmail) {
                setError(
                    "Shop email is unavailable. Please sign in again."
                );

                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError("");

                /*
                 * ==========================================================
                 * SHOP
                 * ==========================================================
                 */

                const resShop =
                    await axios.post(
                        SHOP_API,
                        {
                            email: shopEmail
                                .trim()
                                .toLowerCase(),
                        }
                    );

                if (
                    !resShop?.data ||
                    !resShop.data.shopId
                ) {
                    throw new Error(
                        "Shop could not be found."
                    );
                }

                const shopData =
                    resShop.data;

                setShop(shopData);

                const shopId =
                    shopData.shopId;


                /*
                 * ==========================================================
                 * SALES
                 * ==========================================================
                 */

                try {
                    const resSales =
                        await axios.get(
                            SALES_API,
                            {
                                params: {
                                    shopId,
                                },
                            }
                        );

                    setSales(
                        Array.isArray(
                            resSales.data
                        )
                            ? resSales.data
                            : []
                    );
                } catch (err) {
                    console.error(
                        "❌ Error loading sales:",
                        err?.response?.data ||
                        err?.message ||
                        err
                    );

                    setSales([]);
                }


                /*
                 * ==========================================================
                 * REVIEW SUMMARY
                 * ==========================================================
                 */

                try {
                    const resSummary =
                        await axios.get(
                            REVIEW_SUMMARY_API,
                            {
                                params: {
                                    shopId,
                                },
                            }
                        );

                    setReviewSummary(
                        resSummary.data || null
                    );
                } catch (err) {
                    console.error(
                        "❌ Error loading review summary:",
                        err?.response?.data ||
                        err?.message ||
                        err
                    );

                    setReviewSummary(null);
                }


                /*
                 * ==========================================================
                 * REVIEW HISTORY
                 * ==========================================================
                 */

                try {
                    const resReviews =
                        await axios.get(
                            REVIEWS_API,
                            {
                                params: {
                                    shopId,
                                },
                            }
                        );

                    const reviews =
                        Array.isArray(
                            resReviews.data?.reviews
                        )
                            ? resReviews.data.reviews
                            : [];

                    const sortedReviews =
                        reviews
                            .filter((review) => {
                                const rating =
                                    Number(
                                        review?.rating
                                    );

                                const date =
                                    review?.createdAt
                                        ? new Date(
                                            review.createdAt
                                        )
                                        : null;

                                return (
                                    date &&
                                    !Number.isNaN(
                                        date.getTime()
                                    ) &&
                                    !Number.isNaN(
                                        rating
                                    ) &&
                                    rating >= 1 &&
                                    rating <= 5
                                );
                            })
                            .sort(
                                (a, b) =>
                                    new Date(
                                        a.createdAt
                                    ).getTime() -
                                    new Date(
                                        b.createdAt
                                    ).getTime()
                            );

                    setReviewHistory(
                        sortedReviews
                    );
                } catch (err) {
                    console.error(
                        "❌ Error loading review history:",
                        err?.response?.data ||
                        err?.message ||
                        err
                    );

                    setReviewHistory([]);
                }


                /*
                 * ==========================================================
                 * SHOP IMAGE
                 * ==========================================================
                 */

                try {
                    const resImage =
                        await axios.get(
                            IMAGE_API,
                            {
                                params: {
                                    shopId,
                                },
                            }
                        );

                    if (
                        resImage.data?.imageUrl
                    ) {
                        setImageUrl(
                            resImage.data.imageUrl
                        );
                    } else {
                        setImageUrl("");
                    }
                } catch (err) {
                    console.error(
                        "❌ Error loading shop image:",
                        err?.response?.data ||
                        err?.message ||
                        err
                    );

                    setImageUrl("");
                }


                /*
                 * ==========================================================
                 * PROFILE VIEW ANALYTICS
                 * ==========================================================
                 */

                try {
                    const resAnalytics =
                        await axios.get(
                            PROFILE_ANALYTICS_API,
                            {
                                params: {
                                    shopId,
                                },
                            }
                        );

                    setProfileAnalytics(
                        resAnalytics.data || null
                    );
                } catch (err) {
                    console.error(
                        "❌ Error loading profile analytics:",
                        err?.response?.data ||
                        err?.message ||
                        err
                    );

                    setProfileAnalytics(null);
                }
            } catch (err) {
                console.error(
                    "❌ Error loading overview:",
                    err?.response?.data ||
                    err?.message ||
                    err
                );

                setError(
                    "We could not load your shop overview. Please refresh and try again."
                );
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [shopEmail]);


    const averageRating =
        Number(
            reviewSummary?.averageRating
        ) || 0;

    const totalReviews =
        Number(
            reviewSummary?.totalReviews
        ) || 0;

    const activeSalesCount =
        Array.isArray(sales)
            ? sales.length
            : 0;

    const totalProfileViews =
        profileAnalytics?.totalViews ??
        null;

    const last30DayViews =
        profileAnalytics?.last30Days ??
        null;

    const profileChangePercent =
        profileAnalytics?.changePercent ??
        null;

    const uniqueViewersLast30Days =
        profileAnalytics
            ?.uniqueViewersLast30Days ??
        null;

    const profileHistory =
        Array.isArray(
            profileAnalytics?.history
        )
            ? profileAnalytics.history.map(
                (item) => ({
                    date: item.date,
                    value:
                        Number(item.views) || 0,
                })
            )
            : [];


    const formattedReviewHistory =
        useMemo(() => {
            if (
                !Array.isArray(
                    reviewHistory
                )
            ) {
                return [];
            }

            return reviewHistory
                .map((review) => {
                    const rating =
                        Number(
                            review.rating
                        );

                    if (
                        !review.createdAt ||
                        Number.isNaN(rating)
                    ) {
                        return null;
                    }

                    return {
                        date:
                            review.createdAt.slice(
                                0,
                                10
                            ),

                        value:
                            rating,

                        reviewerName:
                            review.reviewerName ||
                            "",

                        comment:
                            review.comment ||
                            "",
                    };
                })
                .filter(Boolean);
        }, [reviewHistory]);


    const renderStars = (
        rating
    ) => {
        const safeRating =
            Math.max(
                0,
                Math.min(
                    5,
                    Number(rating) || 0
                )
            );

        const rounded =
            Math.round(safeRating);

        return Array.from(
            { length: 5 },
            (_, index) => (
                <span
                    key={index}
                    className={
                        index < rounded
                            ? "overview-star overview-star-active"
                            : "overview-star"
                    }
                >
                    ★
                </span>
            )
        );
    };


    const formatSaleDate = (
        value
    ) => {
        if (!value) return "";

        const date = new Date(value);

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return value;
        }

        return date.toLocaleDateString(
            "en-NZ",
            {
                day: "numeric",
                month: "short",
                year: "numeric",
            }
        );
    };


    const formatReviewDate = (
        value
    ) => {
        if (!value) return "";

        const date = new Date(
            value
        );

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return "";
        }

        return date.toLocaleDateString(
            "en-NZ",
            {
                day: "numeric",
                month: "short",
                year: "numeric",
            }
        );
    };


    const formatProfileChange =
        () => {
            if (
                profileChangePercent ===
                null
            ) {
                if (
                    last30DayViews > 0
                ) {
                    return "New activity this period";
                }

                return "No change yet";
            }

            const value =
                Number(
                    profileChangePercent
                ) || 0;

            if (value > 0) {
                return `↑ ${value.toFixed(
                    1
                )}% vs previous 30 days`;
            }

            if (value < 0) {
                return `↓ ${Math.abs(
                    value
                ).toFixed(
                    1
                )}% vs previous 30 days`;
            }

            return "No change vs previous 30 days";
        };


    const latestReview =
        reviewHistory.length > 0
            ? reviewHistory[
            reviewHistory.length - 1
            ]
            : null;


    if (loading) {
        return (
            <>
                <style>
                    {overviewStyles}
                </style>

                <div className="overview-loading">
                    <div className="overview-spinner" />

                    <div>
                        Loading shop
                        overview...
                    </div>
                </div>
            </>
        );
    }


    if (error) {
        return (
            <>
                <style>
                    {overviewStyles}
                </style>

                <div className="overview-error">
                    <div className="overview-error-icon">
                        !
                    </div>

                    <h3>
                        Unable to load overview
                    </h3>

                    <p>{error}</p>

                    <button
                        type="button"
                        onClick={() =>
                            window.location.reload()
                        }
                    >
                        Refresh page
                    </button>
                </div>
            </>
        );
    }


    if (!shop) {
        return (
            <>
                <style>
                    {overviewStyles}
                </style>

                <div className="overview-error">
                    <h3>
                        Shop not found
                    </h3>

                    <p>
                        We could not find the
                        shop connected to this
                        Portal account.
                    </p>
                </div>
            </>
        );
    }


    return (
        <>
            <style>
                {overviewStyles}
            </style>

            <div className="overview-page">

                {/* INTRO */}

                <section className="overview-intro">
                    <div className="overview-eyebrow">
                        Shop Performance
                    </div>

                    <h1>
                        Your OPPIES overview
                    </h1>

                    <p>
                        See how your shop is
                        performing and keep
                        your key information
                        in one place.
                    </p>
                </section>


                {/* KPI CARDS */}

                <section className="overview-kpi-grid">

                    <div className="overview-kpi-card">
                        <div className="overview-kpi-top">
                            <div>
                                <div className="overview-kpi-label">
                                    Profile Views
                                </div>

                                <div className="overview-kpi-value">
                                    {totalProfileViews !==
                                        null
                                        ? totalProfileViews.toLocaleString(
                                            "en-NZ"
                                        )
                                        : "—"}
                                </div>
                            </div>

                            <div className="overview-kpi-icon">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="22"
                                    height="22"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="2.7"
                                    />
                                </svg>
                            </div>
                        </div>

                        {last30DayViews !==
                            null ? (
                            <>
                                <div className="overview-kpi-meta">
                                    {last30DayViews.toLocaleString(
                                        "en-NZ"
                                    )}{" "}
                                    in the last 30
                                    days
                                </div>

                                <div
                                    className={`overview-kpi-change ${Number(
                                        profileChangePercent
                                    ) > 0
                                            ? "positive"
                                            : Number(
                                                profileChangePercent
                                            ) < 0
                                                ? "negative"
                                                : ""
                                        }`}
                                >
                                    {formatProfileChange()}
                                </div>
                            </>
                        ) : (
                            <div className="overview-kpi-meta">
                                Profile tracking
                                unavailable
                            </div>
                        )}
                    </div>


                    <div className="overview-kpi-card">
                        <div className="overview-kpi-top">
                            <div>
                                <div className="overview-kpi-label">
                                    Reviews
                                </div>

                                <div className="overview-kpi-value">
                                    {totalReviews.toLocaleString(
                                        "en-NZ"
                                    )}
                                </div>
                            </div>

                            <div className="overview-kpi-icon">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="22"
                                    height="22"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
                                </svg>
                            </div>
                        </div>

                        <div className="overview-kpi-meta">
                            Customer reviews
                        </div>
                    </div>


                    <div className="overview-kpi-card">
                        <div className="overview-kpi-top">
                            <div>
                                <div className="overview-kpi-label">
                                    Average Rating
                                </div>

                                <div className="overview-rating-value">
                                    {averageRating
                                        ? averageRating.toFixed(
                                            1
                                        )
                                        : "0.0"}

                                    <span>
                                        ★
                                    </span>
                                </div>
                            </div>

                            <div className="overview-kpi-icon">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="22"
                                    height="22"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="m12 2.8 2.8 5.7 6.3.9-4.6 4.5 1.1 6.3L12 17.3l-5.6 2.9 1.1-6.3-4.6-4.5 6.3-.9Z" />
                                </svg>
                            </div>
                        </div>

                        <div className="overview-kpi-meta">
                            Based on{" "}
                            {totalReviews}{" "}
                            {totalReviews === 1
                                ? "review"
                                : "reviews"}
                        </div>
                    </div>


                    <div className="overview-kpi-card">
                        <div className="overview-kpi-top">
                            <div>
                                <div className="overview-kpi-label">
                                    Active Sales
                                </div>

                                <div className="overview-kpi-value">
                                    {activeSalesCount}
                                </div>
                            </div>

                            <div className="overview-kpi-icon">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="22"
                                    height="22"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M20 12 12 20 4 12V4h8Z" />
                                    <circle
                                        cx="9"
                                        cy="9"
                                        r="1"
                                    />
                                </svg>
                            </div>
                        </div>

                        <div className="overview-kpi-meta">
                            Current sales
                            events
                        </div>
                    </div>

                </section>


                {/* ANALYTICS */}

                <section className="overview-analytics-grid">

                    <div className="overview-panel overview-chart-panel">

                        <div className="overview-panel-heading">
                            <div>
                                <div className="overview-panel-eyebrow">
                                    Engagement
                                </div>

                                <h2>
                                    Profile Views
                                </h2>

                                <p>
                                    Daily profile
                                    opens from the
                                    OPPIES mobile
                                    app.
                                </p>
                            </div>

                            <div className="overview-period-pill">
                                Last 30 days
                            </div>
                        </div>


                        <LineChart
                            data={profileHistory}
                            minY={0}
                            valueFormatter={(
                                value
                            ) =>
                                Math.round(value)
                            }
                            emptyTitle="No profile views yet"
                            emptyText="When shoppers open your shop profile in the OPPIES app, those views will appear here."
                        />


                        {profileAnalytics ? (
                            <div className="overview-chart-footer">
                                <div>
                                    <span>
                                        Last 30 days
                                    </span>

                                    <strong>
                                        {last30DayViews?.toLocaleString(
                                            "en-NZ"
                                        ) || "0"}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Unique viewers
                                    </span>

                                    <strong>
                                        {uniqueViewersLast30Days !==
                                            null
                                            ? uniqueViewersLast30Days.toLocaleString(
                                                "en-NZ"
                                            )
                                            : "—"}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        All-time views
                                    </span>

                                    <strong>
                                        {totalProfileViews !==
                                            null
                                            ? totalProfileViews.toLocaleString(
                                                "en-NZ"
                                            )
                                            : "—"}
                                    </strong>
                                </div>
                            </div>
                        ) : null}
                    </div>


                    <div className="overview-panel overview-chart-panel">

                        <div className="overview-panel-heading">
                            <div>
                                <div className="overview-panel-eyebrow">
                                    Customer Feedback
                                </div>

                                <h2>
                                    Review History
                                </h2>

                                <p>
                                    Historical ratings
                                    received from
                                    OPPIES shoppers.
                                </p>
                            </div>

                            <div className="overview-period-pill">
                                All reviews
                            </div>
                        </div>


                        <LineChart
                            data={
                                formattedReviewHistory
                            }
                            minY={1}
                            maxY={5}
                            valueFormatter={(
                                value
                            ) =>
                                `${Math.round(
                                    value
                                )}★`
                            }
                            emptyTitle="No review history yet"
                            emptyText="Ratings will appear here once shoppers leave reviews for your Op Shop."
                        />


                        {reviewHistory.length > 0 ? (
                            <div className="overview-chart-footer">
                                <div>
                                    <span>
                                        Total reviews
                                    </span>

                                    <strong>
                                        {reviewHistory.length.toLocaleString(
                                            "en-NZ"
                                        )}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Current average
                                    </span>

                                    <strong>
                                        {averageRating.toFixed(
                                            1
                                        )}★
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Latest review
                                    </span>

                                    <strong>
                                        {latestReview
                                            ? `${Number(
                                                latestReview.rating
                                            ).toFixed(
                                                0
                                            )}★`
                                            : "—"}
                                    </strong>
                                </div>
                            </div>
                        ) : null}

                    </div>

                </section>


                {/* SHOP INFORMATION */}

                <section className="overview-section-heading">
                    <div className="overview-eyebrow">
                        Shop Profile
                    </div>

                    <h2>
                        Shop Information
                    </h2>

                    <p>
                        The main information
                        shoppers currently
                        see about your Op
                        Shop.
                    </p>
                </section>


                <section className="overview-shop-card">

                    <div className="overview-shop-details">

                        <div className="overview-detail-row">
                            <div className="overview-detail-icon">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="19"
                                    height="19"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
                                    <circle
                                        cx="12"
                                        cy="10"
                                        r="2.5"
                                    />
                                </svg>
                            </div>

                            <div>
                                <span>
                                    Address
                                </span>

                                <strong>
                                    {shop.address ||
                                        "Not set"}
                                </strong>
                            </div>
                        </div>


                        <div className="overview-detail-row">
                            <div className="overview-detail-icon">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="19"
                                    height="19"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.9a2 2 0 0 1-.5 2.1L8 10a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5 12.5 12.5 0 0 0 2.9.7 2 2 0 0 1 1.7 2Z" />
                                </svg>
                            </div>

                            <div>
                                <span>
                                    Contact
                                </span>

                                <strong>
                                    {shop.contact ||
                                        "Not set"}
                                </strong>
                            </div>
                        </div>


                        <div className="overview-detail-row">
                            <div className="overview-detail-icon">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="19"
                                    height="19"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <rect
                                        x="3"
                                        y="5"
                                        width="18"
                                        height="14"
                                        rx="2"
                                    />
                                    <path d="m3 7 9 6 9-6" />
                                </svg>
                            </div>

                            <div>
                                <span>
                                    Email
                                </span>

                                <strong>
                                    {shop.email ||
                                        shopEmail ||
                                        "Not set"}
                                </strong>
                            </div>
                        </div>

                    </div>


                    <div className="overview-shop-image-wrap">
                        <img
                            src={
                                imageUrl ||
                                "/oppies-logo.png"
                            }
                            alt={
                                shop.shopId ||
                                "OPPIES shop"
                            }
                            className={`overview-shop-image ${!imageUrl
                                    ? "overview-shop-image-placeholder"
                                    : ""
                                }`}
                        />
                    </div>

                </section>


                {/* HOURS + ITEMS */}

                <section className="overview-info-grid">

                    <div className="overview-panel">

                        <div className="overview-panel-heading compact">
                            <div>
                                <div className="overview-panel-eyebrow">
                                    Availability
                                </div>

                                <h2>
                                    Opening Hours
                                </h2>
                            </div>
                        </div>


                        {shop.hours &&
                            typeof shop.hours ===
                            "object" &&
                            Object.keys(shop.hours)
                                .length > 0 ? (
                            <div className="overview-hours-list">
                                {Object.entries(
                                    shop.hours
                                ).map(
                                    ([day, time]) => (
                                        <div
                                            className="overview-hours-row"
                                            key={day}
                                        >
                                            <span>
                                                {day}
                                            </span>

                                            <strong>
                                                {time ||
                                                    "Closed"}
                                            </strong>
                                        </div>
                                    )
                                )}
                            </div>
                        ) : (
                            <div className="overview-simple-empty">
                                No opening hours
                                have been set.
                            </div>
                        )}

                    </div>


                    <div className="overview-panel">

                        <div className="overview-panel-heading compact">
                            <div>
                                <div className="overview-panel-eyebrow">
                                    Shop Range
                                </div>

                                <h2>
                                    Items Offered
                                </h2>
                            </div>
                        </div>


                        {Array.isArray(
                            shop.itemsOffered
                        ) &&
                            shop.itemsOffered
                                .length > 0 ? (
                            <div className="overview-item-tags">
                                {shop.itemsOffered.map(
                                    (item, index) => (
                                        <span
                                            key={`${item}-${index}`}
                                        >
                                            {item}
                                        </span>
                                    )
                                )}
                            </div>
                        ) : (
                            <div className="overview-simple-empty">
                                No items have
                                been listed yet.
                            </div>
                        )}

                    </div>

                </section>


                {/* REVIEW SUMMARY */}

                <section className="overview-review-panel">

                    <div>
                        <div className="overview-panel-eyebrow">
                            Customer Feedback
                        </div>

                        <h2>
                            Review Summary
                        </h2>

                        <p>
                            Your current OPPIES
                            customer rating.
                        </p>
                    </div>


                    <div className="overview-review-score">

                        <div className="overview-review-number">
                            {averageRating.toFixed(
                                1
                            )}
                        </div>

                        <div>
                            <div className="overview-stars">
                                {renderStars(
                                    averageRating
                                )}
                            </div>

                            <div className="overview-review-count">
                                {totalReviews}{" "}
                                {totalReviews === 1
                                    ? "review"
                                    : "reviews"}
                            </div>
                        </div>

                    </div>

                </section>


                {/* LATEST REVIEW */}

                {latestReview ? (
                    <section className="overview-latest-review">

                        <div className="overview-latest-review-heading">
                            <div>
                                <div className="overview-panel-eyebrow">
                                    Most Recent
                                </div>

                                <h2>
                                    Latest Review
                                </h2>
                            </div>

                            <div className="overview-latest-review-rating">
                                {Number(
                                    latestReview.rating
                                ).toFixed(0)}
                                ★
                            </div>
                        </div>

                        <div className="overview-latest-review-meta">
                            <strong>
                                {latestReview.reviewerName ||
                                    "OPPIES shopper"}
                            </strong>

                            <span>
                                {formatReviewDate(
                                    latestReview.createdAt
                                )}
                            </span>
                        </div>

                        {latestReview.comment ? (
                            <p>
                                {latestReview.comment}
                            </p>
                        ) : null}

                    </section>
                ) : null}


                {/* SALES */}

                <section className="overview-sales-section">

                    <div className="overview-section-heading">
                        <div className="overview-eyebrow">
                            Promotions
                        </div>

                        <h2>
                            Current Sales
                        </h2>

                        <p>
                            Sales currently
                            being promoted for
                            your shop.
                        </p>
                    </div>


                    {sales.length > 0 ? (
                        <div className="overview-sales-grid">

                            {sales.map(
                                (sale, index) => (
                                    <div
                                        className="overview-sale-card"
                                        key={
                                            sale.id ||
                                            `${sale.title}-${index}`
                                        }
                                    >
                                        <div className="overview-sale-top">
                                            <div className="overview-sale-tag">
                                                Sale Event
                                            </div>
                                        </div>

                                        <h3>
                                            {sale.title ||
                                                "Shop Sale"}
                                        </h3>

                                        {sale.startDate ||
                                            sale.endDate ? (
                                            <div className="overview-sale-date">
                                                {sale.startDate
                                                    ? formatSaleDate(
                                                        sale.startDate
                                                    )
                                                    : ""}

                                                {sale.startDate &&
                                                    sale.endDate
                                                    ? " → "
                                                    : ""}

                                                {sale.endDate
                                                    ? formatSaleDate(
                                                        sale.endDate
                                                    )
                                                    : ""}
                                            </div>
                                        ) : null}

                                        {sale.description ? (
                                            <p>
                                                {
                                                    sale.description
                                                }
                                            </p>
                                        ) : null}
                                    </div>
                                )
                            )}

                        </div>
                    ) : (
                        <div className="overview-sales-empty">
                            <div>
                                <strong>
                                    No current sales
                                    events
                                </strong>

                                <span>
                                    Any active shop
                                    sales will appear
                                    here.
                                </span>
                            </div>
                        </div>
                    )}

                </section>

            </div>
        </>
    );
};


const overviewStyles = `

  .overview-page {
    color: #1f2937;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }


  .overview-intro {
    margin-bottom: 30px;
  }

  .overview-eyebrow,
  .overview-panel-eyebrow {
    color: #047857;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .overview-intro h1 {
    margin: 7px 0 7px;
    color: #17211c;
    font-size: clamp(26px, 3vw, 36px);
    line-height: 1.08;
    letter-spacing: -0.035em;
  }

  .overview-intro p,
  .overview-section-heading p {
    max-width: 650px;
    margin: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.65;
  }


  .overview-kpi-grid {
    display: grid;
    grid-template-columns:
      repeat(4, minmax(0, 1fr));
    gap: 15px;
    margin-bottom: 18px;
  }

  .overview-kpi-card {
    min-width: 0;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 19px;
    padding: 19px;
    box-shadow:
      0 8px 24px rgba(15, 23, 42, 0.035);
  }

  .overview-kpi-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  }

  .overview-kpi-label {
    margin-bottom: 8px;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
  }

  .overview-kpi-value,
  .overview-rating-value {
    color: #17211c;
    font-size: 30px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.04em;
  }

  .overview-rating-value {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .overview-rating-value span {
    color: #d6aa28;
    font-size: 19px;
  }

  .overview-kpi-icon {
    display: grid;
    place-items: center;
    width: 39px;
    height: 39px;
    flex: 0 0 auto;
    border-radius: 12px;
    background: #ecfdf5;
    color: #047857;
  }

  .overview-kpi-meta {
    margin-top: 13px;
    color: #64748b;
    font-size: 12px;
  }

  .overview-kpi-change {
    margin-top: 5px;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
  }

  .overview-kpi-change.positive {
    color: #047857;
  }

  .overview-kpi-change.negative {
    color: #b45309;
  }


  .overview-analytics-grid {
    display: grid;
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
    gap: 18px;
    margin-bottom: 38px;
  }


  .overview-panel {
    min-width: 0;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    padding: 22px;
    box-shadow:
      0 8px 24px rgba(15, 23, 42, 0.035);
  }


  .overview-chart-panel {
    overflow: hidden;
  }


  .overview-panel-heading {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 20px;
  }

  .overview-panel-heading.compact {
    margin-bottom: 15px;
  }

  .overview-panel-heading h2,
  .overview-section-heading h2,
  .overview-review-panel h2,
  .overview-latest-review h2 {
    margin: 4px 0 5px;
    color: #17211c;
    font-size: 20px;
    line-height: 1.2;
    letter-spacing: -0.025em;
  }

  .overview-panel-heading p,
  .overview-review-panel p {
    margin: 0;
    color: #64748b;
    font-size: 12px;
    line-height: 1.55;
  }

  .overview-period-pill {
    flex: 0 0 auto;
    padding: 7px 10px;
    border: 1px solid #d1fae5;
    border-radius: 999px;
    background: #f0fdf4;
    color: #047857;
    font-size: 10px;
    font-weight: 800;
  }


  .overview-chart-wrap {
    width: 100%;
    min-height: 240px;
  }

  .overview-chart-svg {
    display: block;
    width: 100%;
    height: auto;
    min-height: 220px;
  }

  .overview-chart-empty {
    display: flex;
    min-height: 235px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 28px;
    text-align: center;
  }

  .overview-chart-empty-icon {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    margin-bottom: 12px;
    border-radius: 14px;
    background: #ecfdf5;
    color: #047857;
  }

  .overview-chart-empty-title {
    margin-bottom: 5px;
    color: #1f2937;
    font-size: 13px;
    font-weight: 800;
  }

  .overview-chart-empty-text {
    max-width: 360px;
    color: #94a3b8;
    font-size: 11px;
    line-height: 1.55;
  }


  .overview-chart-footer {
    display: grid;
    grid-template-columns:
      repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 10px;
    padding-top: 16px;
    border-top: 1px solid #eef2f0;
  }

  .overview-chart-footer div {
    min-width: 0;
  }

  .overview-chart-footer span {
    display: block;
    margin-bottom: 3px;
    color: #94a3b8;
    font-size: 10px;
    font-weight: 700;
  }

  .overview-chart-footer strong {
    display: block;
    color: #17211c;
    font-size: 16px;
  }


  .overview-section-heading {
    margin: 0 0 16px;
  }


  .overview-shop-card {
    display: grid;
    grid-template-columns:
      minmax(0, 1fr) 235px;
    gap: 28px;
    align-items: stretch;
    margin-bottom: 18px;
    padding: 22px;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    background: #ffffff;
    box-shadow:
      0 8px 24px rgba(15, 23, 42, 0.035);
  }


  .overview-shop-details {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 19px;
  }

  .overview-detail-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .overview-detail-icon {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    flex: 0 0 auto;
    border-radius: 11px;
    background: #ecfdf5;
    color: #047857;
  }

  .overview-detail-row span {
    display: block;
    margin-bottom: 3px;
    color: #94a3b8;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .overview-detail-row strong {
    display: block;
    overflow-wrap: anywhere;
    color: #334155;
    font-size: 13px;
    font-weight: 700;
  }


  .overview-shop-image-wrap {
    min-height: 180px;
    overflow: hidden;
    border-radius: 16px;
    background: #fbf7ed;
  }

  .overview-shop-image {
    width: 100%;
    height: 100%;
    min-height: 180px;
    object-fit: cover;
  }

  .overview-shop-image-placeholder {
    padding: 28px;
    object-fit: contain;
  }


  .overview-info-grid {
    display: grid;
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
    gap: 18px;
    margin-bottom: 18px;
  }


  .overview-hours-list {
    display: flex;
    flex-direction: column;
  }

  .overview-hours-row {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    padding: 10px 0;
    border-bottom: 1px solid #eef2f0;
    font-size: 12px;
  }

  .overview-hours-row:last-child {
    border-bottom: none;
  }

  .overview-hours-row span {
    color: #64748b;
  }

  .overview-hours-row strong {
    color: #334155;
    text-align: right;
  }


  .overview-item-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .overview-item-tags span {
    padding: 8px 11px;
    border: 1px solid #d1fae5;
    border-radius: 999px;
    background: #f0fdf4;
    color: #047857;
    font-size: 11px;
    font-weight: 700;
  }

  .overview-simple-empty {
    padding: 20px 0;
    color: #94a3b8;
    font-size: 12px;
  }


  .overview-review-panel {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 28px;
    margin-bottom: 18px;
    padding: 22px;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    background:
      linear-gradient(
        135deg,
        #ffffff 0%,
        #fbfdfb 100%
      );
    box-shadow:
      0 8px 24px rgba(15, 23, 42, 0.035);
  }


  .overview-review-score {
    display: flex;
    align-items: center;
    gap: 18px;
  }

  .overview-review-number {
    color: #17211c;
    font-size: 42px;
    font-weight: 800;
    letter-spacing: -0.05em;
  }

  .overview-stars {
    white-space: nowrap;
  }

  .overview-star {
    margin-right: 2px;
    color: #d6d3d1;
    font-size: 18px;
  }

  .overview-star-active {
    color: #d6aa28;
  }

  .overview-review-count {
    margin-top: 4px;
    color: #64748b;
    font-size: 11px;
  }


  .overview-latest-review {
    margin-bottom: 38px;
    padding: 22px;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    background: #ffffff;
    box-shadow:
      0 8px 24px rgba(15, 23, 42, 0.035);
  }

  .overview-latest-review-heading {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 14px;
  }

  .overview-latest-review-rating {
    flex: 0 0 auto;
    padding: 8px 11px;
    border-radius: 999px;
    background: #fbf7ed;
    color: #a16207;
    font-size: 13px;
    font-weight: 800;
  }

  .overview-latest-review-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    color: #64748b;
    font-size: 11px;
  }

  .overview-latest-review-meta strong {
    color: #334155;
  }

  .overview-latest-review-meta span::before {
    content: "•";
    margin-right: 10px;
    color: #cbd5e1;
  }

  .overview-latest-review p {
    margin: 0;
    white-space: pre-line;
    color: #475569;
    font-size: 12px;
    line-height: 1.7;
  }


  .overview-sales-section {
    margin-bottom: 10px;
  }


  .overview-sales-grid {
    display: grid;
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .overview-sale-card {
    padding: 18px;
    border: 1px solid #e7e5df;
    border-radius: 17px;
    background: #fbf7ed;
  }

  .overview-sale-top {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 9px;
  }

  .overview-sale-tag {
    padding: 5px 8px;
    border-radius: 999px;
    background: #ecfdf5;
    color: #047857;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .overview-sale-card h3 {
    margin: 0 0 5px;
    color: #17211c;
    font-size: 15px;
    letter-spacing: -0.015em;
  }

  .overview-sale-date {
    margin-bottom: 9px;
    color: #047857;
    font-size: 10px;
    font-weight: 800;
  }

  .overview-sale-card p {
    margin: 0;
    color: #64748b;
    font-size: 11px;
    line-height: 1.6;
  }


  .overview-sales-empty {
    display: flex;
    min-height: 125px;
    align-items: center;
    justify-content: center;
    padding: 25px;
    border: 1px dashed #d6d3d1;
    border-radius: 18px;
    background: #fcfbf8;
    text-align: center;
  }

  .overview-sales-empty strong {
    display: block;
    margin-bottom: 5px;
    color: #334155;
    font-size: 13px;
  }

  .overview-sales-empty span {
    color: #94a3b8;
    font-size: 11px;
  }


  .overview-loading {
    display: flex;
    min-height: 320px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    color: #64748b;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
    font-size: 13px;
  }

  .overview-spinner {
    width: 31px;
    height: 31px;
    border: 3px solid #d1fae5;
    border-top-color: #047857;
    border-radius: 50%;
    animation:
      overview-spin 0.8s linear infinite;
  }

  @keyframes overview-spin {
    to {
      transform: rotate(360deg);
    }
  }


  .overview-error {
    max-width: 500px;
    margin: 40px auto;
    padding: 30px;
    border: 1px solid #fee2e2;
    border-radius: 20px;
    background: #fff;
    text-align: center;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  .overview-error-icon {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    margin: 0 auto 14px;
    border-radius: 50%;
    background: #fef2f2;
    color: #b91c1c;
    font-weight: 800;
  }

  .overview-error h3 {
    margin: 0 0 7px;
    color: #17211c;
    font-size: 18px;
  }

  .overview-error p {
    margin: 0 0 18px;
    color: #64748b;
    font-size: 12px;
    line-height: 1.6;
  }

  .overview-error button {
    border: none;
    border-radius: 10px;
    padding: 10px 15px;
    background: #047857;
    color: #ffffff;
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
  }


  @media (max-width: 1000px) {

    .overview-kpi-grid {
      grid-template-columns:
        repeat(2, minmax(0, 1fr));
    }

    .overview-analytics-grid {
      grid-template-columns: 1fr;
    }

  }


  @media (max-width: 760px) {

    .overview-shop-card {
      grid-template-columns: 1fr;
    }

    .overview-shop-image-wrap {
      min-height: 220px;
    }

    .overview-info-grid {
      grid-template-columns: 1fr;
    }

    .overview-review-panel {
      align-items: flex-start;
      flex-direction: column;
    }

    .overview-sales-grid {
      grid-template-columns: 1fr;
    }

  }


  @media (max-width: 560px) {

    .overview-intro {
      margin-bottom: 22px;
    }

    .overview-kpi-grid {
      grid-template-columns:
        repeat(2, minmax(0, 1fr));
      gap: 10px;
    }

    .overview-kpi-card {
      padding: 15px;
      border-radius: 16px;
    }

    .overview-kpi-value,
    .overview-rating-value {
      font-size: 25px;
    }

    .overview-kpi-icon {
      width: 34px;
      height: 34px;
    }

    .overview-panel,
    .overview-shop-card,
    .overview-review-panel,
    .overview-latest-review {
      padding: 17px;
      border-radius: 17px;
    }

    .overview-panel-heading {
      gap: 10px;
    }

    .overview-period-pill {
      padding: 6px 8px;
      font-size: 9px;
    }

    .overview-chart-footer {
      gap: 6px;
    }

    .overview-chart-footer strong {
      font-size: 14px;
    }

    .overview-latest-review-heading {
      gap: 10px;
    }

  }


  @media (max-width: 390px) {

    .overview-kpi-grid {
      grid-template-columns: 1fr;
    }

    .overview-panel-heading {
      flex-direction: column;
    }

    .overview-review-score {
      gap: 12px;
    }

    .overview-review-number {
      font-size: 36px;
    }

    .overview-latest-review-heading {
      flex-direction: column;
    }

  }

`;

export default Overview;